// ============================================================================
// CONNECT Portal - SharePoint Service Layer v10
// Added: AssignedRecruiter, OtherCompBonus fields
// ============================================================================

const SHAREPOINT_CONFIG = {
  siteId: 'a24d8c46-4506-41c7-922f-746c5722bc90', 
  lists: {
    requisitions: 'CONNECT_Requisitions',
    approvals: 'CONNECT_Approvals',
    onboarding: 'CONNECT_Onboarding',
    auditLog: 'CONNECT_AuditLog',
    users: 'CONNECT_Users',
  },
  libraries: { attachments: 'CONNECT_Attachments' },
};

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const getSiteUrl = () => `${GRAPH_BASE}/sites/${SHAREPOINT_CONFIG.siteId}`;
const getListUrl = (listName) => `${getSiteUrl()}/lists/${encodeURIComponent(listName)}`;
const getListItemsUrl = (listName) => `${getListUrl(listName)}/items?$expand=fields`;
const getDriveUrl = () => `${getSiteUrl()}/drives`;

const graphRequest = async (url, accessToken, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json', 'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly', ...options.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('Graph API Error:', error);
    throw new Error(error.error?.message || `API request failed: ${response.status}`);
  }
  if (response.status === 204) return null;
  return response.json();
};

const generateRequisitionId = async (accessToken) => {
  try {
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.requisitions)}&$orderby=fields/RequisitionID desc&$top=1`;
    const data = await graphRequest(url, accessToken);
    if (data.value && data.value.length > 0) {
      const lastId = data.value[0].fields.RequisitionID || 'REQ-2024-000';
      const match = lastId.match(/REQ-(\d{4})-(\d{3})/);
      if (match) {
        const year = new Date().getFullYear();
        const nextNum = parseInt(match[2]) + 1;
        return `REQ-${year}-${String(nextNum).padStart(3, '0')}`;
      }
    }
    return `REQ-${new Date().getFullYear()}-001`;
  } catch (err) {
    console.error('Error generating requisition ID:', err);
    return `REQ-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`;
  }
};

const ALLOWED_FILE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.heic', '.pdf', '.doc', '.docx', '.txt', '.rtf', '.odt', '.msg', '.eml', '.xlsx', '.xls', '.csv'];
const BLOCKED_FILE_EXTENSIONS = ['.exe', '.bat', '.cmd', '.ps1', '.sh', '.js', '.vbs', '.msi', '.dll', '.com', '.scr', '.jar', '.py', '.rb', '.php', '.html', '.htm'];

export const FileService = {
  validateFile(file) {
    if (!file || !file.name) return { valid: false, error: 'No file selected' };
    const fileName = file.name.toLowerCase();
    const extension = '.' + fileName.split('.').pop();
    if (BLOCKED_FILE_EXTENSIONS.includes(extension)) return { valid: false, error: `File type "${extension}" is not allowed for security reasons.` };
    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) return { valid: false, error: `File type "${extension}" is not supported.` };
    if (file.size > 4 * 1024 * 1024) return { valid: false, error: `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 4 MB.` };
    return { valid: true };
  },
  async getDriveId(accessToken) {
    const url = getDriveUrl();
    const data = await graphRequest(url, accessToken);
    const drive = data.value.find(d => d.name === SHAREPOINT_CONFIG.libraries.attachments);
    if (!drive) throw new Error('CONNECT_Attachments library not found');
    return drive.id;
  },
  async uploadFile(accessToken, file, folder = '') {
    const validation = this.validateFile(file);
    if (!validation.valid) throw new Error(validation.error);
    const driveId = await this.getDriveId(accessToken);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const safeFileName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const folderPath = folder ? `${folder}/` : '';
    const uploadUrl = `${GRAPH_BASE}/drives/${driveId}/root:/${folderPath}${safeFileName}:/content`;
    const arrayBuffer = await file.arrayBuffer();
    const response = await fetch(uploadUrl, { method: 'PUT', headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': file.type || 'application/octet-stream' }, body: arrayBuffer });
    if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.error?.message || 'File upload failed'); }
    const result = await response.json();
    return { name: result.name, url: result.webUrl, id: result.id };
  },
};

export const UsersService = {
  async getByEmail(accessToken, email) {
    try {
      const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.users)}&$filter=fields/Email eq '${email.toLowerCase()}'`;
      const data = await graphRequest(url, accessToken);
      if (data.value && data.value.length > 0) {
        const item = data.value[0];
        return { spItemId: item.id, email: item.fields.Email, displayName: item.fields.Title, role: item.fields.Role?.toLowerCase() || 'employee', isAdmin: item.fields.IsAdmin || false, isActive: item.fields.IsActive !== false };
      }
      return null;
    } catch (err) { console.error('Error fetching user:', err); return null; }
  },
  async create(accessToken, user) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.users)}/items`;
    const body = { fields: { Title: user.name, Email: user.email.toLowerCase(), Role: 'employee', IsAdmin: false, IsActive: true } };
    const result = await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
    return { spItemId: result.id, email: user.email.toLowerCase(), displayName: user.name, role: 'employee', isAdmin: false, isActive: true };
  },
  async getOrCreate(accessToken, user) {
    let existingUser = await this.getByEmail(accessToken, user.email);
    if (!existingUser) { existingUser = await this.create(accessToken, user); console.log('Created new user:', existingUser.email); }
    return existingUser;
  },
  async getAll(accessToken) {
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.users)}&$orderby=fields/Title`;
    const data = await graphRequest(url, accessToken);
    return data.value.map(item => ({ spItemId: item.id, email: item.fields.Email, displayName: item.fields.Title, role: item.fields.Role?.toLowerCase() || 'employee', isAdmin: item.fields.IsAdmin || false, isActive: item.fields.IsActive !== false }));
  },
  async getRecruiters(accessToken) {
    // Get all users with role = 'recruiter' who are active
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.users)}`;
    const data = await graphRequest(url, accessToken);
    console.log('[SharePoint] All users for recruiter filter:', data.value.map(item => ({ 
      email: item.fields.Email, 
      role: item.fields.Role, 
      isActive: item.fields.IsActive 
    })));
    const recruiters = data.value
      .filter(item => {
        const role = (item.fields.Role || '').toLowerCase();
        const isActive = item.fields.IsActive !== false;
        return role === 'recruiter' && isActive;
      })
      .map(item => ({ 
        spItemId: item.id, 
        email: item.fields.Email, 
        displayName: item.fields.Title, 
        role: 'recruiter' 
      }));
    console.log('[SharePoint] Filtered recruiters:', recruiters);
    return recruiters;
  },
  async update(accessToken, spItemId, updates) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.users)}/items/${spItemId}`;
    const fields = {};
    if (updates.role !== undefined) fields.Role = updates.role;
    if (updates.isAdmin !== undefined) fields.IsAdmin = updates.isAdmin;
    if (updates.isActive !== undefined) fields.IsActive = updates.isActive;
    if (updates.displayName !== undefined) fields.Title = updates.displayName;
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify({ fields }) });
  },
  async getApprovers(accessToken) {
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.users)}`;
    const data = await graphRequest(url, accessToken);
    return data.value.filter(item => ['recruiter', 'ceo', 'cfo'].includes(item.fields.Role?.toLowerCase())).map(item => ({ email: item.fields.Email, displayName: item.fields.Title, role: item.fields.Role?.toLowerCase() }));
  },
};

const mapStatusFromSharePoint = (status) => {
  const map = { 'Draft': 'DRAFT', 'Pending Approval': 'PENDING_APPROVAL', 'PENDING_EXEC_APPROVAL': 'PENDING_EXEC_APPROVAL', 'Approved': 'APPROVED', 'Rejected': 'REJECTED', 'Cancelled': 'CANCELLED', 'On Hold': 'ON_HOLD', 'Recruiting': 'RECRUITING', 'Interviewing': 'INTERVIEWING', 'Verbal Offer': 'VERBAL_OFFER', 'Offer Letter in Progress': 'OFFER_LETTER_IN_PROGRESS', 'Completed': 'COMPLETED' };
  return map[status] || 'DRAFT';
};

const mapStatusToSharePoint = (status) => {
  const map = { 'DRAFT': 'Draft', 'PENDING_APPROVAL': 'Pending Approval', 'PENDING_EXEC_APPROVAL': 'PENDING_EXEC_APPROVAL', 'APPROVED': 'Approved', 'REJECTED': 'Rejected', 'CANCELLED': 'Cancelled', 'ON_HOLD': 'On Hold', 'RECRUITING': 'Recruiting', 'INTERVIEWING': 'Interviewing', 'VERBAL_OFFER': 'Verbal Offer', 'OFFER_LETTER_IN_PROGRESS': 'Offer Letter in Progress', 'COMPLETED': 'Completed' };
  return map[status] || 'Draft';
};

const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

export const RequisitionsService = {
  async getAll(accessToken) {
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.requisitions)}&$orderby=fields/Created desc`;
    const data = await graphRequest(url, accessToken);
    return data.value.map(item => ({
      id: item.fields.RequisitionID, spItemId: item.id, positionTitle: item.fields.PositionTitle, department: item.fields.Department,
      location: item.fields.Location_x002f_Regionfield, roleType: item.fields.RoleType?.toLowerCase() || 'remote',
      requestorName: item.fields.RequestorName, requestorEmail: item.fields.RequestorEmail,
      reasonForRequest: item.fields.ReasonForRequest?.toLowerCase().replace(' ', '') || 'new',
      employmentType: item.fields.EmploymentType?.toLowerCase().replace('-', '') || 'regular',
      projectedStartDate: item.fields.ProjectedStartDate, jobDescription: item.fields.JobDescription,
      skillsRequired: item.fields.SkillsRequired, interviewers: item.fields.Interviewers, remarks: item.fields.Remarks,
      status: mapStatusFromSharePoint(item.fields.Status), createdAt: item.fields.Created,
      cancelledBy: item.fields.CancelledBy, cancelledAt: item.fields.CancelledAt, cancelReason: item.fields.CancelReason,
      salaryRangeInternal: item.fields.SalaryRangeInternal, salaryRangeExternal: item.fields.SalaryRangeExternal,
      priority: item.fields.Priority?.toLowerCase() || 'medium', recruiterNotes: item.fields.RecruiterNotes,
      recruiterNotesUpdatedAt: item.fields.RecruiterNotesUpdatedAt, recruiterNotesUpdatedBy: item.fields.RecruiterNotesUpdatedBy,
      stockOptions: item.fields.StockOptions, bonus: item.fields.Bonus, vacationDays: item.fields.VacationDays,
      negotiatedSalary: item.fields.NegotiatedSalary, negotiatedStockOptions: item.fields.NegotiatedStockOptions,
      negotiatedBonus: item.fields.NegotiatedBonus, negotiatedVacationDays: item.fields.NegotiatedVacationDays,
      preChecklistSignature: item.fields.PreChecklistSignature, preChecklistData: item.fields.PreChecklistData,
      // v10: New fields
      assignedRecruiter: item.fields.AssignedRecruiter || '',
      otherCompBonus: item.fields.OtherCompBonus || '',
      negotiatedOtherCompBonus: item.fields.NegotiatedOtherCompBonus || '',
      approvals: { recruiter: { approved: null, approvedBy: null, approvedAt: null }, ceo: { approved: null, approvedBy: null, approvedAt: null }, cfo: { approved: null, approvedBy: null, approvedAt: null } },
      onboarding: null,
    }));
  },
  async create(accessToken, requisition, user) {
    const requisitionId = await generateRequisitionId(accessToken);
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.requisitions)}/items`;
    const preChecklistJson = requisition.preChecklist ? JSON.stringify(requisition.preChecklist) : '';
    const body = {
      fields: {
        Title: requisitionId, RequisitionID: requisitionId, PositionTitle: requisition.positionTitle, Department: requisition.department,
        Location_x002f_Regionfield: requisition.location, RoleType: capitalizeFirst(requisition.roleType),
        RequestorName: user.name, RequestorEmail: user.email,
        ReasonForRequest: requisition.reasonForRequest === 'new' ? 'New Position' : 'Replacement',
        EmploymentType: capitalizeFirst(requisition.employmentType), ProjectedStartDate: requisition.projectedStartDate,
        JobDescription: requisition.jobDescription, SkillsRequired: requisition.skillsRequired,
        Interviewers: requisition.interviewers, Remarks: requisition.remarks, Status: 'Pending Approval',
        PreChecklistSignature: requisition.preChecklist?.signature || '', PreChecklistData: preChecklistJson,
      },
    };
    await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
    await ApprovalsService.createForRequisition(accessToken, requisitionId);
    await AuditService.log(accessToken, { action: 'Requisition Created', requisitionId, performedBy: user.name, performedByEmail: user.email, details: `Created requisition for ${requisition.positionTitle}. Pre-checklist signed: ${requisition.preChecklist?.signature || 'N/A'}` });
    return requisitionId;
  },
  async updateStatus(accessToken, spItemId, status, user, additionalFields = {}) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.requisitions)}/items/${spItemId}`;
    const body = { fields: { Status: mapStatusToSharePoint(status), ...additionalFields } };
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
  },
  async updateRecruiterFields(accessToken, spItemId, requisitionId, fields, user) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.requisitions)}/items/${spItemId}`;
    const now = new Date().toISOString();
    const body = {
      fields: {
        SalaryRangeInternal: fields.salaryRangeInternal || '', SalaryRangeExternal: fields.salaryRangeExternal || '',
        Priority: capitalizeFirst(fields.priority || 'medium'), RecruiterNotes: fields.recruiterNotes || '',
        RecruiterNotesUpdatedAt: now, RecruiterNotesUpdatedBy: user.name, Status: mapStatusToSharePoint(fields.status),
        StockOptions: fields.stockOptions || '', Bonus: fields.bonus || '', VacationDays: fields.vacationDays || '',
        NegotiatedSalary: fields.negotiatedSalary || '', NegotiatedStockOptions: fields.negotiatedStockOptions || '',
        NegotiatedBonus: fields.negotiatedBonus || '', NegotiatedVacationDays: fields.negotiatedVacationDays || '',
        // v10: New fields
        AssignedRecruiter: fields.assignedRecruiter || '',
        OtherCompBonus: fields.otherCompBonus || '',
        NegotiatedOtherCompBonus: fields.negotiatedOtherCompBonus || '',
      },
    };
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
    await AuditService.log(accessToken, { action: 'Recruiter Update', requisitionId, performedBy: user.name, performedByEmail: user.email, details: `Updated recruiter fields. Status: ${fields.status}${fields.assignedRecruiter ? `. Assigned to: ${fields.assignedRecruiter}` : ''}` });
  },
  async cancel(accessToken, spItemId, requisitionId, reason, user) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.requisitions)}/items/${spItemId}`;
    const body = { fields: { Status: 'Cancelled', CancelledBy: user.name, CancelledAt: new Date().toISOString(), CancelReason: reason } };
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
    await AuditService.log(accessToken, { action: 'Requisition Cancelled', requisitionId, performedBy: user.name, performedByEmail: user.email, details: `Reason: ${reason}` });
  },
};

export const ApprovalsService = {
  async getAll(accessToken) {
    const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.approvals)}`;
    const data = await graphRequest(url, accessToken);
    const approvalsByReq = {};
    data.value.forEach(item => {
      const reqId = item.fields.RequisitionID;
      const role = item.fields.ApproverRole?.toLowerCase();
      if (reqId && role) {
        if (!approvalsByReq[reqId]) approvalsByReq[reqId] = { recruiter: { approved: null, approvedBy: null, approvedAt: null, spItemId: null }, ceo: { approved: null, approvedBy: null, approvedAt: null, spItemId: null }, cfo: { approved: null, approvedBy: null, approvedAt: null, spItemId: null } };
        approvalsByReq[reqId][role] = {
          spItemId: item.id,
          approved: item.fields.ApprovalStatus === 'Approved' ? true : item.fields.ApprovalStatus === 'Rejected' ? false : null,
          approvedBy: item.fields.ApproverName, approvedAt: item.fields.ApprovedAt,
        };
      }
    });
    return approvalsByReq;
  },
  async createForRequisition(accessToken, requisitionId) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.approvals)}/items`;
    for (const role of ['Recruiter', 'CEO', 'CFO']) {
      const body = { fields: { Title: `${requisitionId}-${role}`, RequisitionID: requisitionId, ApproverRole: role, ApprovalStatus: 'Pending' } };
      await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
    }
  },
  async approve(accessToken, spItemId, requisitionId, role, user, approved) {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.approvals)}/items/${spItemId}`;
    const body = { fields: { ApprovalStatus: approved ? 'Approved' : 'Rejected', ApproverName: user.name, ApproverEmail: user.email, ApprovedAt: new Date().toISOString() } };
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
    await AuditService.log(accessToken, { action: approved ? 'Approved' : 'Rejected', requisitionId, performedBy: user.name, performedByEmail: user.email, details: `${role.toUpperCase()} ${approved ? 'approved' : 'rejected'} requisition` });
  },
  async approveOnBehalf(accessToken, spItemId, requisitionId, role, adminUser, approved, justification, attachmentFile) {
    const roleLabel = role.toUpperCase();
    let attachmentUrl = '', attachmentName = '';
    if (attachmentFile) {
      try {
        const folder = `OnBehalfApprovals/${requisitionId}`;
        const uploadResult = await FileService.uploadFile(accessToken, attachmentFile, folder);
        attachmentUrl = uploadResult.url; attachmentName = uploadResult.name;
      } catch (err) { console.error('File upload failed:', err); throw new Error('Failed to upload attachment: ' + err.message); }
    }
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.approvals)}/items/${spItemId}`;
    const body = { fields: { ApprovalStatus: approved ? 'Approved' : 'Rejected', ApproverName: `${adminUser.name} (on behalf of ${roleLabel})`, ApproverEmail: adminUser.email, ApprovedAt: new Date().toISOString(), OnBehalf: true, OnBehalfAdminEmail: adminUser.email, OnBehalfJustification: justification, OnBehalfAttachment: attachmentUrl || attachmentName } };
    await graphRequest(url, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
    await AuditService.log(accessToken, { action: 'Approval On Behalf', requisitionId, performedBy: adminUser.name, performedByEmail: adminUser.email, details: `${roleLabel} ${approved ? 'approved' : 'rejected'} on behalf. Justification: ${justification}` });
  },
};

export const AuditService = {
  async log(accessToken, entry) {
    try {
      const url = `${getListUrl(SHAREPOINT_CONFIG.lists.auditLog)}/items`;
      const body = { fields: { Title: entry.action, Action: entry.action, RequisitionID: entry.requisitionId || '', PerformedBy: entry.performedBy, PerformedByEmail: entry.performedByEmail, Details: entry.details || '', Timestamp: new Date().toISOString() } };
      await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
    } catch (err) { console.error('Audit log error:', err); }
  },
  async getAll(accessToken) {
    try {
      const url = `${getListItemsUrl(SHAREPOINT_CONFIG.lists.auditLog)}&$orderby=fields/Timestamp desc&$top=100`;
      const data = await graphRequest(url, accessToken);
      return data.value.map(item => ({ id: item.id, action: item.fields.Action, requisitionId: item.fields.RequisitionID, performedBy: item.fields.PerformedBy, performedByEmail: item.fields.PerformedByEmail, details: item.fields.Details, timestamp: item.fields.Timestamp }));
    } catch (err) { console.error('Error fetching audit log:', err); return []; }
  },
};

export const DataService = {
  async loadAll(accessToken) {
    const [requisitions, approvalsByReq] = await Promise.all([RequisitionsService.getAll(accessToken), ApprovalsService.getAll(accessToken)]);
    return requisitions.map(req => ({ ...req, approvals: approvalsByReq[req.id] || req.approvals }));
  },
};

const SharePointService = { Users: UsersService, Requisitions: RequisitionsService, Approvals: ApprovalsService, Audit: AuditService, Data: DataService };
export default SharePointService;
